import type { License } from '@wf-coe/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Wire-format License — Date fields are ISO strings. The list endpoint
 * returns just the row columns; the detail endpoint additionally
 * surfaces `secondaryWorkfrontConfigs` (so the License edit modal can
 * pre-fill the multi-select picker without a second round-trip).
 */
export type LicenseDto = Omit<License, 'createdAt' | 'updatedAt'> & {
  createdAt: string;
  updatedAt: string;
  secondaryWorkfrontConfigs?: ReadonlyArray<{ id: string; name: string; url: string }>;
};

/**
 * Wire-format license detail (`GET /v1/clients/{clientId}/licenses/{licenseId}`).
 * Adds `kpiMetricOptions` — the license's own KPI metrics, sorted by display
 * name — so the edit form can offer a main-metric picker without a second route.
 */
export type LicenseDetailDto = LicenseDto & {
  kpiMetricOptions: ReadonlyArray<{ id: number; displayName: string }>;
};

/** Lists licenses for a tenant. Disabled until api + clientId both available. */
export function useLicensesQuery(clientId: string | undefined): UseQueryResult<LicenseDto[]> {
  const api = useApiClient();
  return useQuery({
    queryKey: ['licenses', clientId],
    queryFn: () => {
      if (!api) throw new Error('useLicensesQuery: api client unavailable');
      if (!clientId) throw new Error('useLicensesQuery: clientId required');
      return api.get<LicenseDto[]>(`/v1/clients/${clientId}/licenses`);
    },
    enabled: api !== undefined && clientId !== undefined,
  });
}

/** Single license detail. Disabled until api + clientId + licenseId are all available. */
export function useLicenseQuery(
  clientId: string | undefined,
  licenseId: string | undefined,
): UseQueryResult<LicenseDetailDto> {
  const api = useApiClient();
  return useQuery({
    queryKey: ['licenses', clientId, licenseId],
    queryFn: () => {
      if (!api) throw new Error('useLicenseQuery: api client unavailable');
      if (!clientId) throw new Error('useLicenseQuery: clientId required');
      if (!licenseId) throw new Error('useLicenseQuery: licenseId required');
      return api.get<LicenseDetailDto>(`/v1/clients/${clientId}/licenses/${licenseId}`);
    },
    enabled: api !== undefined && clientId !== undefined && licenseId !== undefined,
  });
}
